import getLoyaltyDisplayTier from 'loyalty/selectors/getLoyaltyDisplayTier'
import { selectLoggedIn } from 'selectors/accountSelectors'
import useLoyaltyChooseDoubleRewards from '../hooks/useLoyaltyChooseDoubleRewards'
import { useAppSelector } from 'hooks/reduxHooks'
import LayoutContainer from 'components/Common/LayoutContainer'
import Group from 'components/utils/Group'
import Sheet from 'components/utils/Sheet'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Drop from 'LuxKit/Drop'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import LoyaltyTierPill from 'loyalty/components/LoyaltyTierPill'
import { rem } from 'polished'
import type { ReactNode } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { CHOOSE_DOUBLE_REWARDS_IMAGE_ID } from '../loyaltyChooseDoubleRewardsTakeover.constants'
import LoyaltyChooseDoubleRewardsLandingPageActiveLabel from './LoyaltyChooseDoubleRewardsLandingPageActiveLabel'
import FormatDateTime from 'components/utils/Formatters/FormatDateTime'
import getLoyaltyProgramDisplayName from 'loyalty/selectors/getLoyaltyProgramDisplayName'

const Content = styled(Group)`
  max-width: ${rem(900)};
  margin: auto;
`

const Copy = styled(Group)`
  flex: 1;
  min-width: 0;
`

const HeroDrop = styled(Drop)`
  width: ${rem(180)};

  ${mediaQueryUp.tablet} {
    width: min(100%, ${rem(330)});
  }
`

function italic(chunks: ReactNode) {
  return <em>{chunks}</em>
}

function LoyaltyChooseDoubleRewardsLandingPageHero({ cta }: { cta: ReactNode }) {
  const intl = useIntl()
  const programName = getLoyaltyProgramDisplayName()
  const loggedIn = useAppSelector(selectLoggedIn)
  const hasAccount = useAppSelector(store => !!store.loyalty.account.data)
  const { memberState, endsAt } = useLoyaltyChooseDoubleRewards()
  const tier = useAppSelector(getLoyaltyDisplayTier)

  const isMember = loggedIn && hasAccount
  const selected = isMember && memberState.status === 'enrolled' ? memberState.choice : undefined
  const showStatusCredits = tier !== 'infinite' || selected === 'statusCredits'
  const endDate = endsAt ? <FormatDateTime value={endsAt} day="numeric" month="long" /> : undefined

  const heading = intl.formatMessage(
    {
      id: 'mJLtmtRTklekQtn-iYTu',
      defaultMessage: 'Your next escape, your choice of <emphasis>double</emphasis> rewards',
      description: 'Hero heading introducing the choose double rewards campaign landing page',
    },
    { emphasis: italic },
  )

  const pointsLabel = intl.formatMessage({
    id: 'C6hMBGoz0PeRo6zo3Qj4',
    defaultMessage: 'Double {programName} Points',
    description: 'First reward option in the double rewards homepage banner',
  }, { programName })

  const orLabel = intl.formatMessage({
    id: 'zUUerhIPjn7wja2y4u4L',
    defaultMessage: 'or',
    description: 'Separator between the two choices on the double rewards homepage banner',
  })

  const statusCreditsLabel = intl.formatMessage({
    id: 'r9RKNXC1RMNCX1Jcd4bp',
    defaultMessage: 'Double Status Credits',
    description: 'Second reward option in the double rewards homepage banner',
  })

  const pointsSubtitle = intl.formatMessage(
    {
      id: 'Ax9IyCENlqMlgmUCLS4z',
      defaultMessage: 'Earn Double {programName} Points on every eligible booking until {date}.',
      description: 'Hero subtitle for a member enrolled in double points on the campaign landing page',
    },
    { programName, date: endDate },
  )
  const creditsSubtitle = intl.formatMessage(
    {
      id: 'ZO26u24I8wmAEAYLfGai',
      defaultMessage: 'Earn Double Status Credits on every eligible booking until {date}.',
      description: 'Hero subtitle for a member enrolled in double status credits on the campaign page',
    },
    { date: endDate },
  )
  const chooseSubtitle = intl.formatMessage(
    {
      id: 'AxYW9QOGVbV-Wlou1Sri',
      defaultMessage: 'Choose your reward and then earn it on every eligible booking until {date}.',
      description: 'Hero subtitle before a customer chooses a reward on the campaign landing page',
    },
    { date: endDate },
  )
  const chooseSubtitleWithoutDate = intl.formatMessage({
    id: 'X5fZDoo72WmwJodwZJ_j',
    defaultMessage: 'Choose your reward and then earn it on every eligible booking.',
    description: 'Hero subtitle when the campaign booking deadline has not been configured yet',
  })

  let subtitle: ReactNode
  if (selected === 'points') {
    subtitle = pointsSubtitle
  } else if (selected === 'statusCredits' && showStatusCredits) {
    subtitle = creditsSubtitle
  } else if (!selected && endDate) {
    subtitle = chooseSubtitle
  } else if (!selected) {
    subtitle = chooseSubtitleWithoutDate
  }

  return (
    <Sheet as="section" background="light-background" padding="32 0">
      <LayoutContainer variant="medium">
        <Content
          direction="vertical-reverse"
          desktopDirection="horizontal"
          horizontalAlign="center"
          desktopHorizontalAlign="start"
          verticalAlign="center"
          gap={20}
          tabletGap={32}
          desktopGap={40}
        >
          <Copy direction="vertical" gap={20} horizontalAlign="center" desktopHorizontalAlign="start">
            <Group
              direction="vertical"
              gap={12}
              horizontalAlign="center"
              desktopHorizontalAlign="start"
              fullWidth
            >
              <Heading variant="heading1" align="center" desktopAlign="start">{heading}</Heading>
              <Group direction="horizontal" gap={8} verticalAlign="center" horizontalAlign="center" desktopHorizontalAlign="start" wrap="wrap">
                {selected !== 'statusCredits' && (
                  <LoyaltyTierPill tier={tier} kind="primary" icon={null}>
                    {pointsLabel}
                  </LoyaltyTierPill>
                )}
                {!selected && showStatusCredits && <BodyText variant="medium">{orLabel}</BodyText>}
                {showStatusCredits && selected !== 'points' && (
                  <LoyaltyTierPill tier={tier} kind="primary" icon={null}>
                    {statusCreditsLabel}
                  </LoyaltyTierPill>
                )}
                {selected && <LoyaltyChooseDoubleRewardsLandingPageActiveLabel endsAt={endsAt} mode="countdown" />}
              </Group>
              <BodyText variant="large" align="center" desktopAlign="start">
                {subtitle}
              </BodyText>
            </Group>
            <Group direction="horizontal">
              {cta}
            </Group>
          </Copy>
          <HeroDrop id={CHOOSE_DOUBLE_REWARDS_IMAGE_ID} size={330} position="bottom-right" />
        </Content>
      </LayoutContainer>
    </Sheet>
  )
}

export default LoyaltyChooseDoubleRewardsLandingPageHero
